import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'Components/frameworks/button/Button';
import { Modal } from 'Components/frameworks/modal/Modal';
import { ID_MODAL_HEADER__FILTERS } from 'Components/utils/identifiers/constants';
import { t } from 'Src/i18n';
import { useAppContext } from 'Context/ReactAppContext';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import {
  getCurrentResort,
  getIsLoadingFilteredAccommodationsAlternativesByComfort,
  getFilteredAccommodationsAlternativesByComfort,
  getAdditionalAccommodations,
} from 'Store/booking/booking.selector';
import { Spinner } from 'Components/frameworks/spinner/Spinner';
import { classnames } from 'Utils/classnames/classnames.utils';
import { RadioButtonWithIcon } from 'Components/widgets/radio-button-with-icon/RadioButtonWithIcon';
import { Formik } from 'formik';
import { Title } from 'Components/frameworks/title/Title';
import {
  getViewIconItem,
  getOutsideFacilitiesIconItem,
  getSurfaceIconItem,
} from 'Components/widgets/accommodations/BookingAccommodationsUtils';
import { ICONS } from 'Components/utils/icon/icons';
import { getFormatedPriceByLocale } from 'Utils/proposal/proposalUtils';
import type { PopinType } from 'Src/types/reservation';
import { popinTypes } from './constants';
import {
  chooseOption,
  addViewIconPropertiesToFilters,
  addOutsideFacilitiesIconPropertiesToFilters,
  addRoomTypeIconPropertiesToFilters,
  getTypedFilters,
  getTitle,
  DefaultAccommodationViewRadioButton,
  DefaultAccommodationOutsideFacilitiesRadioButton,
  DefaultAccommodationRoomTypeRadioButton,
  NO_VIEW_ICON_ID,
  NO_OUTSIDE_FACILITY_ICON_ID,
} from './AccommodationRoomInfos.utils';
type Props = {
  onClose: () => void;
  type: PopinType;
  proposal: Proposal;
};
export const FiltersPopin = ({ type, onClose, proposal }: Props) => {
  const appContext = useAppContext();
  const dispatch = useDispatch();
  const { trackClickEvent } = useTrackEvent();
  const resort = useSelector(getCurrentResort);
  const isLoading = useSelector(getIsLoadingFilteredAccommodationsAlternativesByComfort);
  const alternatives = useSelector(getFilteredAccommodationsAlternativesByComfort);
  const proposalCategoryId = proposal.accommodations[0].categoryId.toString();
  const alternativeByCategory = alternatives?.find(
    ({ categoryId }) => categoryId === proposalCategoryId,
  );
  const accommodationsSets = useSelector(getAdditionalAccommodations) || [];

  if (isLoading) {
    return (
      <Modal
        ariaLabelledby={ID_MODAL_HEADER__FILTERS}
        className="overflow-hidden p-20"
        onClose={onClose}
      >
        <div className="flex items-center justify-center">
          <Spinner size="medium" iconName={ICONS.TRIDENT1} />
        </div>
      </Modal>
    );
  }

  // typed filters
  const filtersView = addViewIconPropertiesToFilters(
    alternativeByCategory?.availableFilters?.view || [],
  );
  const filtersOutsideFacilities = addOutsideFacilitiesIconPropertiesToFilters(
    alternativeByCategory?.availableFilters?.outsideFacilities || [],
  );
  const filtersRoomType = addRoomTypeIconPropertiesToFilters(
    alternativeByCategory?.availableFilters?.roomType || [],
    resort,
  );
  // get the adequate type filters
  const filters = getTypedFilters(
    {
      filtersView,
      filtersOutsideFacilities,
      filtersRoomType,
    },
    type,
  );
  // popin type
  const isViewPopin = type === popinTypes.VIEW;
  const isOutsideFacilitiesPopin = type === popinTypes.OUTSIDE_FACILITIES;
  const isRoomTypePopin = type === popinTypes.ROOM_TYPE;
  // Default Accommodation props :
  // View
  const defaultAccommodationView = proposal.accommodations[0]?.view || {
    id: NO_VIEW_ICON_ID,
    label: t('booking.accommodation.roomFinder.noViewLabel'),
  };
  const defaultAccommodationViewId = defaultAccommodationView?.id;
  const defaultAccommodationHasView =
    !!defaultAccommodationViewId && defaultAccommodationViewId !== NO_VIEW_ICON_ID;
  const defaultAccommodationViewIcon = getViewIconItem(defaultAccommodationView);
  // Outside facilities
  const defaultAccommodationOutsideFacility = proposal.accommodations[0]
    ?.outsideFacilities?.[0] || {
    id: NO_OUTSIDE_FACILITY_ICON_ID,
    label: t('booking.accommodation.roomFinder.noFacilitiesLabel'),
  };
  const defaultAccommodationOutsideFacilityId = defaultAccommodationOutsideFacility?.id;
  const defaultAccommodationHasOutsideFacilities =
    !!defaultAccommodationOutsideFacilityId &&
    defaultAccommodationOutsideFacilityId !== NO_OUTSIDE_FACILITY_ICON_ID;
  const defaultAccommodationOutsideFacilitiesIcon = defaultAccommodationOutsideFacility
    ? getOutsideFacilitiesIconItem([defaultAccommodationOutsideFacility])
    : null;
  // Room type
  const defaultAccommodationRoomType = proposal.accommodations[0]?.roomType; // equals Id

  const defaultAccommodationRoomTypeIcon = getSurfaceIconItem(
    defaultAccommodationRoomType,
    resort,
    proposal.accommodations[0].surface || '35m',
  );
  // current accommodation has nothing from current filter
  const defaultAccommodationHasNothing =
    (!defaultAccommodationHasView && isViewPopin) ||
    (!defaultAccommodationHasOutsideFacilities && isOutsideFacilitiesPopin);

  // add no extra features radio button
  if (defaultAccommodationHasNothing) {
    // IMPORTANT HINT : We always have a default roomType so this part only concerns the view and the outside facilities
    const noOptionTypedItem = {
      id: isViewPopin ? NO_VIEW_ICON_ID : NO_OUTSIDE_FACILITY_ICON_ID,
      firstAlternativeId: '',
      label: isViewPopin
        ? t('booking.accommodation.roomFinder.noViewLabel')
        : t('booking.accommodation.roomFinder.noFacilitiesLabel'),
      icon: {
        icon: (isViewPopin ? NO_VIEW_ICON_ID : NO_OUTSIDE_FACILITY_ICON_ID) as IconName,
        labels: isViewPopin
          ? [t('booking.accommodation.roomFinder.noViewLabel')]
          : [t('booking.accommodation.roomFinder.noFacilitiesLabel')],
      },
    };
    // add this item in the beginning of the array
    filters.unshift(noOptionTypedItem);
  }

  // get the initially selected ID
  const getInitiallySelectedId = () => {
    if (defaultAccommodationHasNothing) {
      return isViewPopin ? NO_VIEW_ICON_ID : NO_OUTSIDE_FACILITY_ICON_ID;
    }

    if (isViewPopin) {
      return defaultAccommodationViewId;
    }

    if (isOutsideFacilitiesPopin) {
      return defaultAccommodationOutsideFacilityId;
    }

    if (isRoomTypePopin) {
      return defaultAccommodationRoomType;
    }
  };

  const initiallySelectedId = getInitiallySelectedId();
  let shouldShowNoPersonalizationMessage = true;

  // Tracking
  const computeOptionCategory = () => {
    if (isViewPopin) {
      return 'room_view';
    }

    if (isOutsideFacilitiesPopin) {
      return 'room_facility';
    }

    if (isRoomTypePopin) {
      return 'room_configuration';
    }

    return '';
  };

  return (
    <Formik
      initialValues={{
        selectedFilterValueId: initiallySelectedId,
      }}
      onSubmit={() => undefined}
    >
      {(formik) => {
        const handleClickValidate = () => {
          const optionCategory = computeOptionCategory();

          const trackRoomPersonalization = (action: 'add' | 'remove') => {
            trackClickEvent(TRACK_EVENT_NAMES.CLICK_ACCOMMODATION, {
              detail_click: `${action}_room_personalization`,
              room_personalization: optionCategory,
            });
          };

          chooseOption({
            appContext,
            formik,
            filters,
            accommodationsSets,
            proposal,
            resort,
            optionCategory,
            dispatch,
            onClose,
            trackRoomPersonalization,
          });
        };

        const title = t(getTitle(type));
        return (
          <Modal
            ariaLabelledby={ID_MODAL_HEADER__FILTERS}
            className="overflow-hidden p-20"
            onClose={onClose}
          >
            <Title
              className="mb-20 justify-center"
              id={ID_MODAL_HEADER__FILTERS}
              theme="title-brand-n2"
            >
              {title}
            </Title>
            <ul
              className={classnames('mb-20 flex flex-wrap gap-10', {
                'justify-center': filters.length < 4,
                'justify-between': filters.length >= 4,
              })}
            >
              {isViewPopin && defaultAccommodationView && (
                <DefaultAccommodationViewRadioButton
                  defaultAccommodationViewIcon={defaultAccommodationViewIcon}
                  value={defaultAccommodationView.id}
                />
              )}
              {isOutsideFacilitiesPopin && defaultAccommodationOutsideFacility && (
                <DefaultAccommodationOutsideFacilitiesRadioButton
                  defaultAccommodationOutsideFacilitiesIcon={
                    defaultAccommodationOutsideFacilitiesIcon
                  }
                  value={defaultAccommodationOutsideFacility.id}
                />
              )}
              {isRoomTypePopin && defaultAccommodationRoomType && (
                <DefaultAccommodationRoomTypeRadioButton
                  defaultAccommodationRoomTypeIcon={defaultAccommodationRoomTypeIcon}
                  value={defaultAccommodationRoomType}
                />
              )}
              {filters?.map(({ id: filterId, icon, firstAlternativeId }, index) => {
                // get the price diff
                const priceDifference = accommodationsSets.find(
                  ({ id }) => id === firstAlternativeId,
                )?.totalDifferentialPrice?.value;
                // +XXX or -XXX
                const formattedPrice = getFormatedPriceByLocale(
                  priceDifference,
                  appContext.locale,
                  proposal.price.currency,
                );

                // here we dont show the alternative of the same type as the current accommodation
                if (
                  (isViewPopin && defaultAccommodationViewId !== filterId) ||
                  (isOutsideFacilitiesPopin &&
                    defaultAccommodationOutsideFacilityId !== filterId) ||
                  (isRoomTypePopin && defaultAccommodationRoomType !== filterId)
                ) {
                  shouldShowNoPersonalizationMessage = false;
                  return (
                    <li className="w-150 flex-none" key={index}>
                      <RadioButtonWithIcon
                        isChecked={formik?.values?.selectedFilterValueId === filterId}
                        name="selectedFilterValueId"
                        title={icon.labels[0]}
                        icon={icon.icon}
                        description={icon.labels?.[1] || ''}
                        value={filterId}
                        priceDifference={formattedPrice}
                      />
                    </li>
                  );
                }
              })}
              {shouldShowNoPersonalizationMessage && (
                <li className="flex w-150 flex-none items-center justify-center">
                  {t('booking.accommodation.roomFinder.noPersonalizationErrorMessage')}
                </li>
              )}
            </ul>

            <div className="-mx-20 mt-20 flex justify-center border-t-1 border-lightGrey pt-20">
              <Button onClick={handleClickValidate}>{t('booking.common.validate')}</Button>
            </div>
          </Modal>
        );
      }}
    </Formik>
  );
};
